import * as React from 'react';
import {emptyObject, constant} from '@packages/utilities/fp/utils';
import {
  type Type,
  matchType,
  getMeta,
  Plugins,
  Variable,
  Types,
  TypeImplementation,
  setType,
  getSystem,
  withSystem,
  withMeta,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {usePlugins} from '@packages/utilities/platform';
import {Text} from '@packages/systems/spring';
import {getBindingLabel} from '@packages/systems/ExpressionEditor-utils';

const dataTypeSummaryWithType = matchType<
  (
    arg1: ContextualExpression,
    arg2: Plugins.PluginsType
  ) => React.ReactElement | null
>({
  TypeConstructor:
    (name) => (context: ContextualExpression, plugins: Plugins.PluginsType) =>
      summaryForPluginType(Types.getByName(plugins, name), context),
  TypeApplication:
    ({con}) =>
    (context: ContextualExpression) => (
      <DataTypeSummary dataType={con} context={context} />
    ),
  Generic:
    ({body}) =>
    (context: ContextualExpression) => (
      <DataTypeSummary dataType={body} context={context} />
    ),
  _: constant(() => null),
});

const unwrapAliasTypeWithSystemAndMeta = (
  type: Type,
  plugins: Plugins.PluginsType
) => {
  const ty = Types.getAliasType(type, plugins);
  // If there was no Alias type return the original type
  if (!ty) return type;
  const system = getSystem(type);
  const meta = getMeta(type);
  const typeWithSystem = system ? withSystem(system, ty) : ty;
  return meta ? withMeta(meta, typeWithSystem) : typeWithSystem;
};

export const DataTypeSummary = ({
  context,
  dataType,
}: {
  context: ContextualExpression;
  dataType?: Type;
}) => {
  const plugins = usePlugins();

  const ty = dataType ? dataType : getAnn(context).type;

  if (!ty) {
    return null;
  }

  // If we encounter an Alias render a unique component from it via
  // `renderSummary` or unwrap the alias and call `DataTypeSummary` again.
  const tyImpl = Types.getImplementation(ty, plugins);
  if (tyImpl && tyImpl instanceof TypeImplementation.AliasTypeImplementation) {
    const unwrappedAliasExpr = setType(
      context,
      unwrapAliasTypeWithSystemAndMeta(ty, plugins)
    );
    const SummaryComponent = TypeImplementation.getRenderSummary(tyImpl);
    if (SummaryComponent) {
      return <SummaryComponent expr={unwrappedAliasExpr} />;
    }
    return <DataTypeSummary context={unwrappedAliasExpr} />;
  }

  // If we encounter a binding render a specific purple text
  // for its binding label
  if (ContextualExpressions.isBinding(stripAnn(context))) {
    const envTypes = getAnn(context).env.context.values;
    const bindingLabel = getBindingLabel(envTypes, stripAll(context));
    return <Text colorVariant="purple">{bindingLabel}</Text>;
  }

  if (Types.isElementType(ty)) {
    const element = ContextualExpressions.getElement(stripAnn(context));
    if (!element) {
      return <span />;
    }
    return <DataTypeSummary context={element.data} />;
  }

  const fields = Types.getRecordFields(ty);
  const meta = getMeta(ty);

  if (fields) {
    const contextRecord = ContextualExpressions.getRecord(stripAnn(context));
    if (!contextRecord) return <span />;
    if (
      meta &&
      meta.renderSummaryKeys &&
      Array.isArray(meta.renderSummaryKeys)
    ) {
      return (
        <>
          {meta.renderSummaryKeys.map<React.ReactNode>((summaryPart, idx) => {
            if (!summaryPart || typeof summaryPart.key !== 'string') {
              return <span key={idx} />;
            }
            const newContext = contextRecord[summaryPart.key];
            const newDataType = fields[summaryPart.key];
            if (!newContext || !newDataType) {
              return <span key={idx} />;
            }
            const addons = summaryPart.addons || emptyObject;
            const prefix =
              typeof addons.prefix === 'string' ? addons.prefix : '';
            const postfix =
              typeof addons.postfix === 'string' ? addons.postfix : '';
            return (
              <span key={idx}>
                {prefix}
                <DataTypeSummary context={newContext} dataType={newDataType} />
                {postfix}
              </span>
            );
          })}
        </>
      );
    }
    // If `renderSummaryKeys` is not defined return a default summary
    return <span>Missing renderSummary for Record.</span>;
  }

  const unionValues = Types.getUnionValues(ty);
  if (unionValues) {
    const union = ContextualExpressions.getUnion(stripAnn(context));
    if (!union) return <span />;
    return <span>{union.tag}</span>;
  }

  const listType = Types.getListItemType(ty);
  if (listType) {
    const contextItems = ContextualExpressions.getList(stripAnn(context));
    if (
      contextItems &&
      meta &&
      meta.renderSummaryIndexes &&
      Array.isArray(meta.renderSummaryIndexes)
    ) {
      return (
        <>
          {meta.renderSummaryIndexes.map<React.ReactNode>(
            (summaryPart, idx) => {
              const newContext = contextItems[summaryPart.index];
              const newDataType = listType;
              if (!newContext || !newDataType) {
                return <span key={idx} />;
              }
              return (
                <span key={idx}>
                  {summaryPart.addons && summaryPart.addons.prefix}
                  <DataTypeSummary
                    context={newContext}
                    dataType={newDataType}
                  />
                  {summaryPart.addons && summaryPart.addons.postfix}
                </span>
              );
            }
          )}
        </>
      );
    }
    // If `renderSummaryIndexes` is not defined return a default summary
    if (!contextItems) {
      return <span />;
    }
    const firstItem = contextItems[0];
    if (!firstItem) {
      return <span />;
    }
    return (
      <span>
        <DataTypeSummary context={firstItem} dataType={listType} />
        {contextItems.length > 1 && (
          <span> + {contextItems.length - 1} more</span>
        )}
      </span>
    );
  }

  return dataTypeSummaryWithType(ty)(context, plugins);
};

const summaryForPluginType = (
  pluginType: null | TypeImplementation.TypeImplementation,
  context: ContextualExpression
) => {
  if (!pluginType) return <span />;

  const SummaryComponent = TypeImplementation.getRenderSummary(pluginType);
  if (SummaryComponent === undefined) {
    const variable = ContextualExpressions.getVariable(stripAnn(context));
    if (variable) {
      return <span>{Variable.getName(variable)}</span>;
    }
    return <span>No summary defined</span>;
  }
  return <SummaryComponent expr={context} />;
};
